import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon, Upload as UploadIcon } from '@mui/icons-material';
import { deleteField, doc, getDoc, getDocs, limit, query, collection, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useTenant } from '../../providers/TenantProvider';
import type { Tenant } from '../../lib/db/types';
import { SettingsShell } from './SettingsShell';

const DEFAULT_TIMEZONE = 'America/New_York';
const DEFAULT_PRIMARY_COLOR = '#1976d2';

const timeZoneOptions = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

type AddressForm = NonNullable<NonNullable<Tenant['businessProfile']>['address']>;
type GeneralSettingsTenant = Partial<Omit<Tenant, 'defaultTimezone' | 'businessProfile' | 'businessLogic' | 'branding'>> & {
  defaultTimezone?: string | null;
  businessProfile?: Tenant['businessProfile'] | null;
  businessLogic?: Tenant['businessLogic'] | null;
  branding?: Tenant['branding'] | null;
};

interface GeneralSettingsForm {
  name: string;
  website: string;
  email: string;
  phone: string;
  defaultTimezone: string;
  primaryColor: string;
  logoUrl: string;
  address: Required<AddressForm>;
}

const emptyAddress: Required<AddressForm> = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  country: 'US',
};

function tenantToForm(tenant: GeneralSettingsTenant | null | undefined): GeneralSettingsForm {
  const businessProfile = tenant?.businessProfile || {};
  const address = businessProfile.address || {};

  return {
    name: tenant?.name || '',
    website: businessProfile.website || '',
    email: businessProfile.email || '',
    phone: businessProfile.phone || '',
    defaultTimezone: tenant?.defaultTimezone || DEFAULT_TIMEZONE,
    primaryColor: tenant?.branding?.primaryColor || DEFAULT_PRIMARY_COLOR,
    logoUrl: tenant?.branding?.logoUrl || '',
    address: {
      ...emptyAddress,
      ...address,
    },
  };
}

function compactAddress(address: Required<AddressForm>): AddressForm {
  return {
    line1: address.line1.trim(),
    line2: address.line2.trim(),
    city: address.city.trim(),
    region: address.region.trim(),
    postalCode: address.postalCode.trim(),
    country: address.country.trim() || 'US',
  };
}

function formatAddress(address: Required<AddressForm>) {
  const locality = [address.city, address.region, address.postalCode].filter(Boolean).join(' ');
  return [address.line1, address.line2, locality, address.country].filter(Boolean).join(', ');
}

function fileExtension(file: File) {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension && /^[a-z0-9]+$/.test(extension) ? extension : 'png';
}

function readFileBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const [, base64 = ''] = result.split(',');
      if (!base64) {
        reject(new Error('Could not read logo file'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read logo file'));
    reader.readAsDataURL(file);
  });
}

export function GeneralSettingsPage() {
  const { tenantId, activeTenant, refreshTenants } = useTenant();
  const [form, setForm] = useState<GeneralSettingsForm>(() => tenantToForm(activeTenant || null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [primaryLocationId, setPrimaryLocationId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const logoPreviewUrl = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    if (removeLogo) return '';
    return form.logoUrl;
  }, [form.logoUrl, logoFile, removeLogo]);

  useEffect(() => {
    return () => {
      if (logoFile) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoFile, logoPreviewUrl]);

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [tenantSnap, locationsSnap] = await Promise.all([
          getDoc(doc(db, 'tenants', tenantId)),
          getDocs(query(collection(db, 'locations'), where('tenantId', '==', tenantId), limit(1))),
        ]);

        if (!mounted) return;

        const tenantData = tenantSnap.exists()
          ? ({ tenantId, ...tenantSnap.data() } as Tenant)
          : (activeTenant as GeneralSettingsTenant | null);
        const nextForm = tenantToForm(tenantData);
        const primaryLocation = locationsSnap.docs[0];
        setPrimaryLocationId(primaryLocation?.id || null);

        if (primaryLocation?.exists()) {
          const locationData = primaryLocation.data();
          const locationAddress = locationData.location as Partial<AddressForm> | undefined;
          setForm({
            ...nextForm,
            defaultTimezone: tenantData?.defaultTimezone || locationData.timezone || DEFAULT_TIMEZONE,
            address: {
              ...nextForm.address,
              ...(locationAddress || {}),
            },
          });
        } else {
          setForm(nextForm);
        }
      } catch (error) {
        console.error('Error loading general settings:', error);
        setMessage({ type: 'error', text: 'Failed to load general settings.' });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      mounted = false;
    };
  }, [activeTenant, tenantId]);

  const handleFieldChange = (field: keyof GeneralSettingsForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleAddressChange = (field: keyof Required<AddressForm>, value: string) => {
    setForm((current) => ({
      ...current,
      address: {
        ...current.address,
        [field]: value,
      },
    }));
  };

  const handleLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Logo must be an image file.' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Logo must be smaller than 2MB.' });
      return;
    }

    setLogoFile(file);
    setRemoveLogo(false);
  };

  const handleSave = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found.' });
      return;
    }

    if (!form.name.trim()) {
      setMessage({ type: 'error', text: 'Gym name is required.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      let nextLogoUrl = form.logoUrl;
      if (logoFile) {
        const uploadTenantLogo = httpsCallable(functions, 'uploadTenantLogo');
        const result = await uploadTenantLogo({
          tenantId,
          fileBase64: await readFileBase64(logoFile),
          contentType: logoFile.type,
          extension: fileExtension(logoFile),
          fileSize: logoFile.size,
        });
        nextLogoUrl = (result.data as { logoUrl: string }).logoUrl;
      }

      const address = compactAddress(form.address);
      const tenantRef = doc(db, 'tenants', tenantId);
      const tenantPayload: Record<string, unknown> = {
        name: form.name.trim(),
        defaultTimezone: form.defaultTimezone || DEFAULT_TIMEZONE,
        businessProfile: {
          website: form.website.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address,
        },
        'branding.primaryColor': form.primaryColor,
        updatedAt: Timestamp.now(),
      };

      if (removeLogo) {
        tenantPayload['branding.logoUrl'] = deleteField();
      } else if (nextLogoUrl) {
        tenantPayload['branding.logoUrl'] = nextLogoUrl;
      }

      await updateDoc(tenantRef, tenantPayload);

      const locationId = primaryLocationId || `${tenantId}_main`;
      const now = Timestamp.now();
      const locationPayload: Record<string, unknown> = {
        tenantId,
        name: 'Main Location',
        timezone: form.defaultTimezone || DEFAULT_TIMEZONE,
        address: formatAddress(form.address),
        location: address,
        updatedAt: now,
      };
      if (!primaryLocationId) {
        locationPayload.createdAt = now;
      }

      await setDoc(
        doc(db, 'locations', locationId),
        locationPayload,
        { merge: true }
      );

      setPrimaryLocationId(locationId);
      setLogoFile(null);
      setRemoveLogo(false);
      setForm((current) => ({
        ...current,
        name: current.name.trim(),
        website: current.website.trim(),
        email: current.email.trim(),
        phone: current.phone.trim(),
        logoUrl: removeLogo ? '' : nextLogoUrl,
        address: {
          ...emptyAddress,
          ...address,
        },
      }));
      await refreshTenants();
      setMessage({ type: 'success', text: 'General settings saved.' });
    } catch (error: any) {
      console.error('Error saving general settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save general settings.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsShell title="General Settings">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={800}>
              Gym Profile
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Gym name"
                  value={form.name}
                  onChange={(event) => handleFieldChange('name', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  type="url"
                  value={form.website}
                  onChange={(event) => handleFieldChange('website', event.target.value)}
                  placeholder="https://example.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(event) => handleFieldChange('email', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={form.phone}
                  onChange={(event) => handleFieldChange('phone', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Default timezone"
                  value={form.defaultTimezone}
                  onChange={(event) => handleFieldChange('defaultTimezone', event.target.value)}
                  helperText="Used as the default for schedules, locations, booking displays, and notifications."
                >
                  {timeZoneOptions.map((timezone) => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={800}>
              Brand
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary color"
                  type="color"
                  value={form.primaryColor}
                  onChange={(event) => handleFieldChange('primaryColor', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={logoPreviewUrl || undefined}
                    variant="rounded"
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: form.primaryColor,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {form.name.charAt(0).toUpperCase() || 'G'}
                  </Avatar>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button component="label" variant="outlined" startIcon={<UploadIcon />}>
                      Upload logo
                      <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
                    </Button>
                    {(form.logoUrl || logoFile) && (
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => {
                          setLogoFile(null);
                          setRemoveLogo(true);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={800}>
              Address
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street address"
                  value={form.address.line1}
                  onChange={(event) => handleAddressChange('line1', event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address line 2"
                  value={form.address.line2}
                  onChange={(event) => handleAddressChange('line2', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={form.address.city}
                  onChange={(event) => handleAddressChange('city', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State / region"
                  value={form.address.region}
                  onChange={(event) => handleAddressChange('region', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal code"
                  value={form.address.postalCode}
                  onChange={(event) => handleAddressChange('postalCode', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Country"
                  value={form.address.country}
                  onChange={(event) => handleAddressChange('country', event.target.value)}
                />
              </Grid>
            </Grid>
          </Stack>

          <Box>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? undefined : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save General Settings'}
            </Button>
          </Box>
        </Stack>
      )}
    </SettingsShell>
  );
}
